import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth/auth.service';
import { SKIP_GLOBAL_LOADER } from '../../auth/loader.interceptor';
import { SKIP_ERROR_TOAST } from '../../auth/error.interceptor';

/**
 * Counts screen views per workspace for Super Admin > Usage & Billing. Sends
 * only the route shape (ids replaced by ":id"), never query strings or data.
 */
@Injectable({ providedIn: 'root' })
export class PageUsageService {
  private router = inject(Router);
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private started = false;
  private last = { page: '', at: 0 };

  start(): void {
    if (this.started) return;
    this.started = true;
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.track(e.urlAfterRedirects));
  }

  private track(url: string): void {
    if (!this.auth.isLoggedIn()) return;
    // A super admin browsing a workspace would skew that client's numbers.
    if (this.auth.isSuperAdmin()) return;

    const page = PageUsageService.normalise(url);
    if (!page) return;

    // Redirect chains and double navigations shouldn't count twice.
    const now = Date.now();
    if (page === this.last.page && now - this.last.at < 3000) return;
    this.last = { page, at: now };

    this.http
      .post(
        `${environment.usage}/page-view`,
        { page },
        {
          context: new HttpContext().set(SKIP_GLOBAL_LOADER, true).set(SKIP_ERROR_TOAST, true),
        },
      )
      .subscribe({ error: () => undefined });
  }

  /** "/grn/3f2b...ab/edit?x=1" -> "/grn/:id/edit"; "/" -> null. */
  static normalise(url: string): string | null {
    const path = String(url ?? '').split(/[?#;]/)[0].toLowerCase();
    const page = path
      .split('/')
      .map((seg) =>
        /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(seg) || /^\d+$/.test(seg) ? ':id' : seg,
      )
      .join('/')
      .replace(/\/+$/, '');
    return page && page !== '/' ? page : null;
  }
}
